/**
 * ClinicStatusScreen
 *
 * Asks preserve-goal users whether they're already at a clinic.
 * Maps to fertilityIntent.stage on the API:
 * - no-clinic → exploring-options
 * - researching → exploring-options
 * - at-clinic → getting-ready
 */

import React from "react";
import { View, Text, Pressable } from "react-native";
import { Compass, Search, Building2 } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { PlanBuilderState } from "../types";

type ClinicStatusValue = NonNullable<PlanBuilderState["clinicStatus"]>;

type Props = {
  onNext: () => void;
};

const OPTIONS: {
  value: ClinicStatusValue;
  labelKey: string;
  sublabelKey: string;
  Icon: typeof Compass;
}[] = [
  {
    value: "no-clinic",
    labelKey: "plan_builder.clinic_status.no_clinic_label",
    sublabelKey: "plan_builder.clinic_status.no_clinic_sublabel",
    Icon: Compass,
  },
  {
    value: "researching",
    labelKey: "plan_builder.clinic_status.researching_label",
    sublabelKey: "plan_builder.clinic_status.researching_sublabel",
    Icon: Search,
  },
  {
    value: "at-clinic",
    labelKey: "plan_builder.clinic_status.at_clinic_label",
    sublabelKey: "plan_builder.clinic_status.at_clinic_sublabel",
    Icon: Building2,
  },
];

export default function ClinicStatusScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ClinicStatusScreen");
  const { state, updateState } = usePlanBuilder();

  const handleSelect = (value: ClinicStatusValue) => {
    updateState({ clinicStatus: value });
  };

  return (
    <View style={{ padding: 24, paddingBottom: 40 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.clinic_status.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.clinic_status.subtitle")}
      </Text>

      <View style={{ gap: 10 }}>
        {OPTIONS.map((opt) => {
          const isSelected = state.clinicStatus === opt.value;
          return (
            <Pressable
              key={opt.value}
              onPress={() => {
                capturePress(pressLabel(t(opt.labelKey)), opt.value);
                handleSelect(opt.value);
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 14,
                  paddingVertical: 16,
                  paddingHorizontal: 18,
                  backgroundColor: isSelected
                    ? `${EMBER.colors.text}08`
                    : "rgba(255, 255, 255, 0.82)",
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: isSelected
                    ? EMBER.colors.text
                    : "rgba(255, 255, 255, 0.9)",
                }}
              >
                <opt.Icon
                  size={20}
                  color={isSelected ? EMBER.colors.text : EMBER.colors.textMid}
                  strokeWidth={1.5}
                />
                <View style={{ flex: 1 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: isSelected ? "600" : "500",
                      color: EMBER.colors.text,
                    }}
                  >
                    {t(opt.labelKey)}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textLight,
                      marginTop: 2,
                    }}
                  >
                    {t(opt.sublabelKey)}
                  </Text>
                </View>
              </View>
            </Pressable>
          );
        })}
      </View>

      {state.clinicStatus && (
        <View style={{ marginTop: 24 }}>
          <WarmButton
            title={t("plan_builder.continue")}
            onPress={() => onNext()}
          />
        </View>
      )}
    </View>
  );
}
